export const ORGANIZATION_SORTABLE = [
  'name',
  'slug',
  'members',
  'created_at',
  'updated_at',
] as const

export const ORGANIZATION_STATES = ['enabled', 'disabled'] as const

export const ORGANIZATION_LIMITS = { name: 100, slug: 60, logo: 500 } as const

export type OrganizationSort = (typeof ORGANIZATION_SORTABLE)[number]

export const PASSWORD_MINIMUM = 8

export const PASSWORD_MAXIMUM = 200

export const passwordRefusal = (value: string): string | undefined =>
  value.length < PASSWORD_MINIMUM ? 'errors.password_too_short' : undefined

export const isStrongPassword = (value: string): boolean => passwordRefusal(value) === undefined

export const PROFILE_LIMITS = {
  name: 100,
  passwordMin: PASSWORD_MINIMUM,
  passwordMax: PASSWORD_MAXIMUM,
} as const

export const ACTIONS = ['insert', 'update', 'delete'] as const

export const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'CHF'] as const

export const BASE = CURRENCIES[0]

export type Currency = (typeof CURRENCIES)[number]

export const AUDIT_KINDS = [
  '_auth_adm_users',
  '_auth_crm_users',
  '_auth_adm_members',
  '_auth_crm_members',
  '_auth_organizations',
  'users',

  '_auth_adm_accounts',
  '_auth_crm_accounts',
  '_auth_adm_verifications',
  '_auth_crm_verifications',
] as const

export const AUDIT_REALMS = ['adm', 'crm'] as const

export const AUDIT_SORTABLE = ['created_at', 'who', 'action', 'kind'] as const

export const REALMS = ['crm', 'adm'] as const

export const ROLE_NAMES = ['owner', 'admin', 'member'] as const

export const ASSIGNABLE_ROLES_BY_REALM = {
  crm: ['owner', 'admin', 'member'],
  adm: [],
} as const satisfies Record<Realm, readonly string[]>

export const ASSIGNABLE_ROLES = ['owner', 'admin', 'member'] as const

export const MANAGED_USER_SORTABLE = ['name', 'email', 'created_at', 'updated_at'] as const

export const MANAGED_USER_LIMITS = {
  name: 100,
  email: 200,
  passwordMin: PASSWORD_MINIMUM,
  passwordMax: PASSWORD_MAXIMUM,
} as const

export const EXPORT_CEILING = 10_000

export const EXPORT_SCOPES = ['viewed', 'all'] as const

export type ExportScope = (typeof EXPORT_SCOPES)[number]

export const overCeiling = (count: number): { path: string; message: string }[] =>
  count > EXPORT_CEILING ? [{ path: '/scope', message: 'errors.export_too_large' }] : []

export type Realm = (typeof REALMS)[number]

export type RoleName = (typeof ROLE_NAMES)[number]

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number]

export const assignableRolesFor = (realm: Realm): readonly RoleName[] =>
  ASSIGNABLE_ROLES_BY_REALM[realm]

export const ROLE_SEPARATOR = ', '

export const roleNamesOf = (role: string, say: (key: string) => string): string =>
  role === ''
    ? say('audit.blank')
    : role
        .split(ROLE_SEPARATOR)
        .map((name) => say(`adm.users.roles.${name}`))
        .join(ROLE_SEPARATOR)

export const assignsRoles = (realm: Realm): boolean =>
  ASSIGNABLE_ROLES_BY_REALM[realm].length > 0

export const realmsOffering = (role: string): Realm[] =>
  REALMS.filter((realm) => (ASSIGNABLE_ROLES_BY_REALM[realm] as readonly string[]).includes(role))

const ORPHAN_ROLE_REALM: Realm = 'crm'

export const boardRolesFor = (realm: Realm): RoleName[] =>
  ROLE_NAMES.filter((role) => (realmsOffering(role)[0] ?? ORPHAN_ROLE_REALM) === realm)

export const MEMBER_OPERATORS_GRANT = 'users.operate_members'

export const ADMIN_OPERATORS_GRANT = 'users.operate_admins'

export const raisesInto = (realm: Realm) =>
  realm === 'adm' ? ADMIN_OPERATORS_GRANT : MEMBER_OPERATORS_GRANT

export type ManagedUserSort = (typeof MANAGED_USER_SORTABLE)[number]

export type Action = (typeof ACTIONS)[number]

export type AuditKind = (typeof AUDIT_KINDS)[number]

export type AuditSort = (typeof AUDIT_SORTABLE)[number]

export const CRM_AUDIT_KINDS = [
  'customers',
  'campaigns',
  'vouchers',
  'rewards',
  'earning_rules',
  'segments',
  'organizations',
] as const

export const CRM_AUDIT_SORTABLE = ['created_at', 'who', 'action', 'kind'] as const

export const JOB_BROWSABLE_KINDS = [
  'point_activation',
  'point_expiry',
  'webhook_delivery',
  'rate_limit_sweep',
  'notification_send',
  'notification_sweep',
] as const

export const JOB_CREDENTIAL_KINDS = ['password_setup', 'email_verification'] as const

export const JOB_KINDS = [...JOB_BROWSABLE_KINDS, ...JOB_CREDENTIAL_KINDS] as const

export const JOB_RAW_ERROR_KINDS = [
  'rate_limit_sweep',
  'notification_sweep',
  'webhook_delivery_sweep',
  'point_activation',
  'point_expiry',
] as const

export const REDACTED_JOB_ERROR = 'delivery_failed'

export const JOB_STATES = [
  'created',
  'retry',
  'active',
  'completed',
  'cancelled',
  'failed',
] as const

export const JOB_SHOWN_ARGS = ['status', 'deliveryId', 'voucherId', 'transactionId', 'lotId'] as const

export const JOB_SORTABLE = ['created_at', 'kind', 'state'] as const

export const MEMBER_SORTABLE = ['created_at', 'name', 'email', 'realm'] as const

export const MEMBER_REALMS = ['crm'] as const

export const NOTIFICATION_KINDS = [
  'points.accrued',
  'points.expiring',
  'reward.redeemed',
] as const

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number]

export const NOTIFICATION_CHANNELS = ['inapp', 'email'] as const

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

export const NOTIFICATION_DEFAULTS = {
  'points.accrued': { realms: ['crm'], inapp: true, email: false },
  'points.expiring': { realms: ['crm'], inapp: true, email: false },
  'reward.redeemed': { realms: ['crm'], inapp: true, email: false },
} as const satisfies Record<
  NotificationKind,
  { realms: readonly MemberRealm[] } & Record<NotificationChannel, boolean>
>

export type NotificationPreference = {
  kind: NotificationKind
  channel: NotificationChannel
  enabled: boolean
}

export const notifiesRealm = (kind: string, realm: MemberRealm): boolean =>
  NOTIFICATION_DEFAULTS[kind as NotificationKind]?.realms.some((named) => named === realm) ?? false

export const notificationDefaults = (realm: MemberRealm): NotificationPreference[] =>
  NOTIFICATION_KINDS.filter((kind) => notifiesRealm(kind, realm)).flatMap((kind) =>
    NOTIFICATION_CHANNELS.map((channel) => ({
      kind,
      channel,
      enabled: NOTIFICATION_DEFAULTS[kind][channel],
    })),
  )

export const notificationDefaultFor = (kind: string, channel: NotificationChannel): boolean =>
  NOTIFICATION_DEFAULTS[kind as NotificationKind]?.[channel] ?? false

export type CrmAuditKind = (typeof CRM_AUDIT_KINDS)[number]

export type CrmAuditSort = (typeof CRM_AUDIT_SORTABLE)[number]

export type JobShownArg = (typeof JOB_SHOWN_ARGS)[number]

export type JobKind = (typeof JOB_KINDS)[number]

export const showsRawJobError = (kind: string): boolean =>
  (JOB_RAW_ERROR_KINDS as readonly string[]).includes(kind)

export type JobState = (typeof JOB_STATES)[number]

export type JobSort = (typeof JOB_SORTABLE)[number]

export type MemberSort = (typeof MEMBER_SORTABLE)[number]

export type MemberRealm = (typeof MEMBER_REALMS)[number]

export const ORGANIZATION_PROFILE_FIELDS = [
  'nip',
  'address_l1',
  'address_l2',
  'country',
  'email',
  'phone',
] as const

export const ORGANIZATION_PROFILE_LIMITS = {
  nip: 10,
  address_l1: 200,
  address_l2: 200,
  country: 2,
  email: 255,
  phone: 40,
} as const

const NAME_LETTER = /\p{L}/u

export const isPersonName = (value: string): boolean => NAME_LETTER.test(value)

const PHONE_SEPARATORS = /[\s().-]/g

export const PHONE_DIGITS_MIN = 11

export const PHONE_DIGITS_MAX = 15

export const normalisePhone = (value: string): string => value.replace(PHONE_SEPARATORS, '')

export const isPhone = (value: string): boolean => {
  const normalised = normalisePhone(value)

  if (!normalised.startsWith('+')) return false

  const digits = normalised.slice(1)

  return (
    /^[0-9]+$/.test(digits) &&
    digits.length >= PHONE_DIGITS_MIN &&
    digits.length <= PHONE_DIGITS_MAX
  )
}

const POSTAL_CODE = /^[0-9]{2}-[0-9]{3}$/

export const isPostalCode = (value: string): boolean => POSTAL_CODE.test(value)

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isEmail = (value: string): boolean => EMAIL.test(value)

export const CAMPAIGN_TYPES = [
  'LOYALTY_PROGRAM',
  'DISCOUNT_COUPONS',
  'GIFT_VOUCHERS',
  'REFERRAL_PROGRAM',
  'PROMOTION',
] as const

export type CampaignType = (typeof CAMPAIGN_TYPES)[number]

export const CAMPAIGN_STATUSES = ['DRAFT', 'DONE'] as const

export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

export const VOUCHER_TYPES = ['LOYALTY_CARD', 'DISCOUNT_VOUCHER', 'GIFT_VOUCHER'] as const

export type VoucherType = (typeof VOUCHER_TYPES)[number]

export const ORDER_STATUSES = ['CREATED', 'PAID', 'CANCELED', 'FULFILLED'] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

export const POINT_TRANSACTION_TYPES = [
  'POINTS_ACCRUAL',
  'POINTS_REDEMPTION',
  'POINTS_EXPIRATION',
  'POINTS_CANCELLATION',
  'POINTS_ADJUSTMENT',
] as const

export type PointTransactionType = (typeof POINT_TRANSACTION_TYPES)[number]

export const POINT_LOT_STATES = ['PENDING', 'ACTIVE', 'EXPIRED', 'CANCELED'] as const

export type PointLotState = (typeof POINT_LOT_STATES)[number]

export const REDEMPTION_RESULTS = ['SUCCESS', 'FAILURE'] as const

export type RedemptionResult = (typeof REDEMPTION_RESULTS)[number]

export const REDEMPTION_STATUSES = ['SUCCEEDED', 'FAILED', 'ROLLED_BACK'] as const

export type RedemptionStatus = (typeof REDEMPTION_STATUSES)[number]

export const EVENT_CATEGORIES = ['ACTION', 'EFFECT'] as const

export type EventCategory = (typeof EVENT_CATEGORIES)[number]

export const REWARD_TYPES = ['CAMPAIGN', 'MATERIAL', 'COIN'] as const

export type RewardType = (typeof REWARD_TYPES)[number]

export const EARNING_TRIGGERS = ['CUSTOM_EVENT', 'ORDER_PAID', 'SEGMENT_ENTRY'] as const

export type EarningTrigger = (typeof EARNING_TRIGGERS)[number]

export const EARNING_CAP_SCOPES = [
  'PER_CUSTOMER_TOTAL',
  'PER_CUSTOMER_PER_DAY',
  'PER_CUSTOMER_PER_SEASON',
] as const

export type EarningCapScope = (typeof EARNING_CAP_SCOPES)[number]

export const SEGMENT_KINDS = ['static', 'auto'] as const

export type SegmentKind = (typeof SEGMENT_KINDS)[number]

export const WEBHOOK_DELIVERY_STATES = ['PENDING', 'DELIVERED', 'FAILED', 'ABANDONED'] as const

export type WebhookDeliveryState = (typeof WEBHOOK_DELIVERY_STATES)[number]

export const CRM_CUSTOMER_SORTABLE = [
  'created_at',
  'source_id',
  'email',
  'name',
  'phone',
  'birthdate',
  'updated_at',
] as const

export type CrmCustomerSort = (typeof CRM_CUSTOMER_SORTABLE)[number]

export const CRM_CAMPAIGN_SORTABLE = [
  'created_at',
  'name',
  'campaign_type',
  'active',
  'start_date',
  'expiration_date',
  'updated_at',
] as const

export type CrmCampaignSort = (typeof CRM_CAMPAIGN_SORTABLE)[number]

export const CRM_VOUCHER_SORTABLE = [
  'created_at',
  'code',
  'voucher_type',
  'campaign',
  'active',
  'start_date',
  'expiration_date',
  'redemption_redeemed_quantity',
  'updated_at',
] as const

export type CrmVoucherSort = (typeof CRM_VOUCHER_SORTABLE)[number]

export const CRM_POINT_TRANSACTION_SORTABLE = [
  'created_at',
  'transaction_type',
  'points',
  'balance_after',
  'voucher',
  'campaign',
  'source_id',
] as const

export type CrmPointTransactionSort = (typeof CRM_POINT_TRANSACTION_SORTABLE)[number]

export const CRM_ORDER_SORTABLE = [
  'created_at',
  'source_id',
  'status',
  'amount',
  'discount_amount',
  'customer',
  'updated_at',
] as const

export type CrmOrderSort = (typeof CRM_ORDER_SORTABLE)[number]

export const CRM_REDEMPTION_SORTABLE = [
  'created_at',
  'customer',
  'result',
  'status',
  'failure_code',
  'points_spent',
  'balance_after',
] as const

export type CrmRedemptionSort = (typeof CRM_REDEMPTION_SORTABLE)[number]

export const CRM_EVENT_SORTABLE = ['created_at', 'name', 'category', 'customer'] as const

export type CrmEventSort = (typeof CRM_EVENT_SORTABLE)[number]

export const CRM_REWARD_SORTABLE = [
  'created_at',
  'name',
  'reward_type',
  'source_campaign',
  'updated_at',
] as const

export type CrmRewardSort = (typeof CRM_REWARD_SORTABLE)[number]

export const CRM_EARNING_RULE_SORTABLE = [
  'created_at',
  'campaign',
  'trigger_type',
  'event_name',
  'active',
  'points_fixed',
  'updated_at',
] as const

export type CrmEarningRuleSort = (typeof CRM_EARNING_RULE_SORTABLE)[number]

export const CRM_SEGMENT_SORTABLE = [
  'created_at',
  'name',
  'segment_kind',
  'members',
  'updated_at',
] as const

export type CrmSegmentSort = (typeof CRM_SEGMENT_SORTABLE)[number]

export const CRM_SEGMENT_MEMBER_SORTABLE = [
  'added_at',
  'source_id',
  'email',
  'name',
  'phone',
] as const

export type CrmSegmentMemberSort = (typeof CRM_SEGMENT_MEMBER_SORTABLE)[number]

export const CRM_PRODUCT_SORTABLE = [
  'created_at',
  'source_id',
  'name',
  'price',
  'updated_at',
] as const

export type CrmProductSort = (typeof CRM_PRODUCT_SORTABLE)[number]

export const CRM_VALIDATION_RULE_SORTABLE = ['created_at', 'name', 'campaigns', 'updated_at'] as const

export type CrmValidationRuleSort = (typeof CRM_VALIDATION_RULE_SORTABLE)[number]

export const CRM_WEBHOOK_SUBSCRIPTION_SORTABLE = [
  'created_at',
  'target_url',
  'active',
  'updated_at',
] as const

export type CrmWebhookSubscriptionSort = (typeof CRM_WEBHOOK_SUBSCRIPTION_SORTABLE)[number]

export type Localized = { pl: string; en: string }

export const STREAMING_IDLE_TIMEOUT = 255
